import { AuthService } from '@/services/auth';
import { LEGAL_VERSIONS } from '@court-sync/shared';
import type { UserFormData } from './publicBooking/schemas';
import { requestPasswordResetWithFallback } from './academyAuthHandlers';

export { requestPasswordResetWithFallback };

export type ClubRegisterSubmitInput = {
  data: UserFormData;
  returnPath: string;
};

export type ClubRegisterSubmitResult =
  | {
      type: 'success';
      email: string;
      returnUrl: string;
      verificationEmailFailed?: boolean;
    }
  | { type: 'login'; email: string; message: string }
  | { type: 'error'; message: string };

/**
 * Club public registration creates a platform user only.
 * Member role assignment happens during membership join checkout, not here.
 */
export async function submitClubRegister(
  input: ClubRegisterSubmitInput
): Promise<ClubRegisterSubmitResult> {
  const { data, returnPath } = input;
  const displayName = `${data.firstName.trim()} ${data.lastName.trim()}`.trim();

  try {
    const { verificationEmailFailed } = await AuthService.signUpWithEmail(
      data.email,
      data.password,
      displayName,
      undefined,
      undefined,
      returnPath,
      {
        termsAccepted: data.termsAccepted,
        privacyAccepted: data.privacyAccepted,
        termsVersion: LEGAL_VERSIONS.TERMS,
        privacyVersion: LEGAL_VERSIONS.PRIVACY,
        marketingEmailConsent: data.marketingEmailConsent || false,
      }
    );

    return {
      type: 'success',
      email: data.email,
      returnUrl: returnPath,
      verificationEmailFailed,
    };
  } catch (authError: unknown) {
    const err = authError as { code?: string; message?: string };
    if (err.code === 'auth/email-already-in-use') {
      return {
        type: 'login',
        email: data.email,
        message: 'An account with this email already exists. Please sign in to continue.',
      };
    }
    return { type: 'error', message: err.message || 'Could not create account' };
  }
}
